import React from "react";
import { Box, Typography, Link } from "@mui/material";
import {
  ContentCopy,
  OpenInNew,
  PhoneIphone,
  Computer,
} from "@mui/icons-material";
import AppleCalendarUpsell from "./AppleCalendarUpsell.js";

const AppleFAQs = ({ onCopyLink, calendar }) => {
  return [
    {
      question: "I wasn't prompted to add the calendar.",
      answer: (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h5"
              sx={{
                mb: 2,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <PhoneIphone sx={{ fontSize: 20 }} />
              iPhone
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 1: </strong>
              <Link
                onClick={onCopyLink}
                sx={{ cursor: "pointer", textDecoration: "underline" }}
              >
                Copy this link
                <ContentCopy sx={{ fontSize: 14, ml: 0.5 }} />
              </Link>
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 2: </strong>
              Open your Apple Calendar app
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 3: </strong>
              Tap <b>Calendars</b> on the bottom
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 4: </strong>
              Tap <b>Add Calendar</b> on the bottom left and select{" "}
              <b>Add Subscription Calendar</b>
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 5: </strong>
              Paste the link you copied and select <b>Add</b> on the top right.
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 6: </strong>
              <Link
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                sx={{ cursor: "pointer", textDecoration: "underline" }}
              >
                Confirm that everything is working
              </Link>{" "}
              as expected or{" "}
              <Link
                onClick={() =>
                  window.open(
                    `https://docs.google.com/forms/d/e/${process.env.REACT_APP_SUPPORT_FORM_ID}/viewform`,
                    "_blank"
                  )
                }
                sx={{ cursor: "pointer", textDecoration: "underline" }}
              >
                submit a ticket
                <OpenInNew sx={{ fontSize: 14, ml: 0.5 }} />
              </Link>{" "}
              if you continue to have issues.
            </Typography>

            <Typography
              variant="h5"
              sx={{
                mt: 4,
                mb: 2,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Computer sx={{ fontSize: 20 }} />
              Mac
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 1: </strong>
              <Link
                onClick={onCopyLink}
                sx={{ cursor: "pointer", textDecoration: "underline" }}
              >
                Copy this link
                <ContentCopy sx={{ fontSize: 14, ml: 0.5 }} />
              </Link>
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 2: </strong>
              Open your Calendar app on Mac
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 3: </strong>
              Go to <b>File</b> → <b>New Calendar Subscription</b>
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 4: </strong>
              Paste the link you copied and click <b>Subscribe</b>
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 5: </strong>
              <Link
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                sx={{ cursor: "pointer", textDecoration: "underline" }}
              >
                Confirm that everything is working
              </Link>{" "}
              as expected or{" "}
              <Link
                onClick={() =>
                  window.open(
                    `https://docs.google.com/forms/d/e/${process.env.REACT_APP_SUPPORT_FORM_ID}/viewform`,
                    "_blank"
                  )
                }
                sx={{ cursor: "pointer", textDecoration: "underline" }}
              >
                submit a ticket
                <OpenInNew sx={{ fontSize: 14, ml: 0.5 }} />
              </Link>{" "}
              if you continue to have issues.
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      question: "I don't see the calendar or upcoming events.",
      answer: (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h5"
              sx={{
                mb: 2,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <PhoneIphone sx={{ fontSize: 20 }} />
              iPhone
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 1: </strong>
              Open your Apple Calendar app
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 2: </strong>
              Tap <b>Calendars</b> at the bottom of the screen
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 3: </strong>
              Look for your subscribed calendar under the <b>Subscribed</b>{" "}
              section
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 4: </strong>
              Make sure there's a <b>checkmark</b> next to your calendar name.
              If not, tap it to enable it.
            </Typography>

            <Typography
              variant="h5"
              sx={{
                mt: 4,
                mb: 2,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Computer sx={{ fontSize: 20 }} />
              Mac
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 1: </strong>
              Open your Calendar app on Mac
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 2: </strong>
              Look at the left sidebar for your subscribed calendar
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 3: </strong>
              Check if there's a <b>checkmark</b> next to your calendar name in
              the sidebar. If not, click the checkbox to enable it.
            </Typography>

            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Step 4: </strong>
              Your calendar should appear under <b>Subscriptions</b> or{" "}
              <b>Other</b> in the sidebar.
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      question: "My events aren't updating.",
      answer: (
        <>
          Apple Calendar can take 24-48 hours to sync calendar subscriptions.
          You can try refreshing manually:
          <br />
          <br />
          <Box
            sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}
          >
            <PhoneIphone sx={{ fontSize: 16, mt: 0.2 }} />
            <Typography variant="body1">
              <b>iPhone:</b> Tap on <b>Calendars</b> on the bottom. Pull down on
              the list of calendars to refresh.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Computer sx={{ fontSize: 16, mt: 0.2 }} />
            <Typography variant="body1">
              <b>Mac:</b> Press Command+R to refresh your calendars.
            </Typography>
          </Box>
          <AppleCalendarUpsell calendar={calendar} />
        </>
      ),
    },
  ];
};

export default AppleFAQs;
